/**
 * Color Resolver Utility
 * Manages theme-based colors with CSS variable support
 * 
 * Strategy:
 * - Store: CSS variable names (theme-aware, adapts to theme changes)
 * - Display: CSS variables (for dynamic theming)
 * - API Submit: CSS variables (themes understand them)
 * - NO CONVERSIONS: Keep color system flexible and theme-aware
 */

import { themes } from '../themes/tokens';

/**
 * Default color variable to use for new events/calendars
 * Stored as CSS variable for theme consistency
 */
export const DEFAULT_COLOR_VARIABLE = 'var(--color-info-dark)';

/**
 * Color options for user selection
 * Each option has a variable name and display label
 */
export const COLOR_PALETTE = [
  { variable: 'var(--color-primary)', label: 'Primary' },
  { variable: 'var(--color-primary-dark)', label: 'Primary Dark' },
  { variable: 'var(--color-info)', label: 'Info' },
  { variable: 'var(--color-info-dark)', label: 'Info Dark' },
  { variable: 'var(--color-success)', label: 'Success' },
  { variable: 'var(--color-success-dark)', label: 'Success Dark' },
  { variable: 'var(--color-warning)', label: 'Warning' },
  { variable: 'var(--color-warning-dark)', label: 'Warning Dark' },
  { variable: 'var(--color-error)', label: 'Error' },
  { variable: 'var(--color-error-dark)', label: 'Error Dark' },
  { variable: 'var(--color-secondary)', label: 'Secondary' },
  { variable: 'var(--color-secondary-dark)', label: 'Secondary Dark' },
];

/**
 * Gets the default color variable
 * Returns CSS variable for theme consistency
 * @returns {string} CSS variable like "var(--color-info-dark)"
 */
export function getDefaultEventColor() {
  return DEFAULT_COLOR_VARIABLE;
}

/**
 * Validates if a value is a CSS variable
 * @param {string} value - Value to check
 * @returns {boolean} True if valid CSS variable format
 */
export function isCSSVariable(value) {
  return typeof value === 'string' && /^var\(--[\w-]+\)$/.test(value);
}

/**
 * Validates if a color is a proper hex color code
 * @param {string} color - Color to validate
 * @returns {boolean} True if valid hex color
 */
export function isValidHexColor(color) {
  return typeof color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(color);
}

/**
 * Validates a color value (CSS variable or hex)
 * @param {string} color - Color value to validate
 * @returns {boolean} True if valid CSS variable or hex color
 */
export function isValidColor(color) {
  return isCSSVariable(color) || isValidHexColor(color);
}
